import React, { useEffect, useState } from 'react';
import { useSubscriptionTenant } from '../../../hooks/subscription';
import Loading from '../../Shared/ResourceIndex/Loading';
import SubscriptionCard from '../../Subscription/SubscriptionCard';
import { ActionButtons } from '../../Shared/ActionButtons';
import ChooseSubscriptionModal from './ChooseSubscriptionModal';
import { ConfirmationModal, Modal } from '@sw-sw/lib-ui';
import subscriptionApi from '../../../utils/api/subscription';

export default function SubscriptionTab({ role }: { role: string }) {
  const [showChooseSubscriptionModal, setShowChooseSubscriptionModal] =
    useState(false);
  const [showCancelSubscriptionModal, setShowCancleSubscriptionModal] =
    useState(false);
  const [showAddOnConfirmationModal, setShowAddOnConfirmationModal] =
    useState(false);
  const [addOnList, setAddOnList] = useState<any[]>([]);
  const [addOnId, setAddOnId] = useState<number | null>(null);
  const [addOnEnabled, setAddOnEnabled] = useState<boolean | null>(null);
  const [addOnPrice, setAddOnPrice] = useState<number | null>(null);
  const [totalSelectedAddOnsPrice, setTotalSelectedAddOnsPrice] = useState(0);
  const subscriptionTenantQuery = useSubscriptionTenant();

  useEffect(() => {
    if (!subscriptionTenantQuery.data) return;
    if (!subscriptionTenantQuery.data.subscription) return;
    const updatedAddOns = subscriptionTenantQuery.data.subscription.addons.map(
      (addon: any) => {
        const tenantAddon = subscriptionTenantQuery.data.addons.find(
          (tenantAddon: any) => tenantAddon.subscription_addon_id === addon.id,
        );

        return { ...addon, is_enabled_tenant: !!tenantAddon };
      },
    );

    setAddOnList(updatedAddOns);

    const totalSelectedAddOnsPrice = updatedAddOns
      .filter(
        (addon: any) =>
          addon.is_enabled_tenant && addon.as_per_project_usage === false,
      )
      .reduce(
        (acc: number, addon: any) => Number(acc) + Number(addon.price),
        0,
      );

    setTotalSelectedAddOnsPrice(totalSelectedAddOnsPrice);
  }, [subscriptionTenantQuery.data]);

  if (!subscriptionTenantQuery.data || subscriptionTenantQuery.isLoading) {
    return <Loading what='Subscriptions' />;
  }

  const handleOnCheckAddOn = (
    id: number,
    is_enabled: boolean,
    price: number,
  ) => {
    setAddOnId(id);
    setAddOnEnabled(is_enabled);
    setAddOnPrice(price);
    setShowAddOnConfirmationModal(true);
  };

  const handleOnConfirmAddOn = () => {
    subscriptionApi
      .chooseAddOn(
        subscriptionTenantQuery.data.tenantSubscriptionId,
        addOnId!,
        !addOnEnabled!,
      )
      .then(() => {
        subscriptionTenantQuery.refetch();
        setShowAddOnConfirmationModal(false);
      });
  };

  return (
    <div className='subscription-container'>
      <div className='subscription-container__header'>
        <h3>Current Subscription</h3>
      </div>

      {subscriptionTenantQuery.data.addons &&
        subscriptionTenantQuery.data.addons.length > 2 && (
          <div className='subscription-container__help-info'>
            <i className='fa fa-info-circle' aria-hidden='true'></i>
            <p>
              To get the best price available we recommend contacting our sales
              team at support@sw2.net
            </p>
          </div>
        )}

      {subscriptionTenantQuery.data.subscription && (
        <SubscriptionCard
          name={subscriptionTenantQuery.data.subscription.name}
          description={subscriptionTenantQuery.data.subscription.description}
          price={
            Number(subscriptionTenantQuery.data.subscription.price) +
            Number(totalSelectedAddOnsPrice)
          }
          addOnList={addOnList}
          subscriptionType={
            subscriptionTenantQuery.data.subscription.subscription_type
          }
          type={'change'}
          handleButtonClick={() => setShowChooseSubscriptionModal(true)}
          handleOnCheckAddOn={handleOnCheckAddOn}
          handleCancelSubscription={() => setShowCancleSubscriptionModal(true)}
        />
      )}

      {!subscriptionTenantQuery.data.subscription && (
        <div className='subscription-container__no-subscription'>
          <p>No subscription found</p>
          <ActionButtons
            className='subscription-container__button'
            primary='Choose Plan'
            onClick={() => setShowChooseSubscriptionModal(true)}
          />
        </div>
      )}

      {showChooseSubscriptionModal && (
        <ChooseSubscriptionModal
          isNew={subscriptionTenantQuery.data.subscription === undefined}
          setShowModal={setShowChooseSubscriptionModal}
          role={role}
          subscriptionTenantQuery={subscriptionTenantQuery}
          currentTenantSubscriptionId={
            subscriptionTenantQuery.data &&
            subscriptionTenantQuery.data.tenantSubscriptionId
          }
        />
      )}

      <Modal
        show={showAddOnConfirmationModal && !!addOnEnabled}
        title='Disable Add On'
        cancelBtn={false}
        submitBtnText='Ok'
        handleSubmit={() => setShowAddOnConfirmationModal(false)}
        handleClose={() => setShowAddOnConfirmationModal(false)}
      >
        <p>Self-service Add On removal is coming soon. For now, please email our <i>
          <u>
            <a
              href="mailto:support@sw2.net"
              style={{ color: "inherit", textDecoration: "underline" }}
            >
              <strong>subscription specialist</strong>
            </a>
          </u>
        </i> (or call 1-720-456-2420) and we’ll be happy to adjust your plan for you.</p>
      </Modal>

      <Modal
        show={showCancelSubscriptionModal}
        title='Cancle Subscription'
        cancelBtn={false}
        submitBtnText='Ok'
        handleSubmit={() => setShowCancleSubscriptionModal(false)}
        handleClose={() => setShowCancleSubscriptionModal(false)}
      >
        <p>Self-cancle subscription is coming soon. For now, please email our <i>
          <u>
            <a
              href="mailto:support@sw2.net"
              style={{ color: "inherit", textDecoration: "underline" }}
            >
              <strong>subscription specialist</strong>
            </a>
          </u>
        </i> (or call 1-720-456-2420) and we’ll be happy to help you.</p>
      </Modal>

      <ConfirmationModal
        show={showAddOnConfirmationModal && !addOnEnabled}
        title={'Enable Add On'}
        subTitle={
          `Are you sure you want to enable this add on it will add $${addOnPrice} to your subscription plan.`
        }
        handleClose={() => setShowAddOnConfirmationModal(false)}
        handleConfirm={handleOnConfirmAddOn}
      />
    </div>
  );
}
